import type { Bounds, Camera, StageSize } from './camera';

/**
 * The minimap, as arithmetic (V0.53.4).
 *
 * A small picture of the whole visible map at the top right of the stage, with a frame over
 * the part the camera is showing. Everything about it that is a number lives here, DOM-free,
 * for the reason `camera.ts` gives for the camera: the component that draws it should be a
 * thin shell around functions a test can call without a browser.
 *
 * Four questions, no state:
 *   - how big is the card, given the map's shape (`minimapBox`)
 *   - what part of world space does it show (`minimapViewBox`)
 *   - where is the frame — the camera's INVERSE (`frameOf`)
 *   - where does the camera go when somebody clicks a point (`cameraCentredOn`)
 *
 * The numbers were chosen from a proof of concept the user tried every variant in
 * (2026-09-22): a 300 px longest side, a card that follows the map's shape rather than a fixed
 * box, and a fog over everything outside the frame in addition to its outline.
 */

/** The card's LONGEST side, in CSS pixels. The other follows the map's shape. */
export const MINIMAP_SIDE = 300;

/**
 * How far the card's shape may follow the map's: between 1:2 and 2:1.
 *
 * The map is usually much taller than it is wide once a few branches are open. Following its
 * shape exactly would make the card a 300 × 40 sliver whose marks are unreadable; clamping the
 * aspect keeps it a picture, and `minimapViewBox` fills the spare room with empty world so
 * nothing is stretched.
 */
export const MINIMAP_ASPECT_MIN = 0.5;
export const MINIMAP_ASPECT_MAX = 2;

/** Breathing room around the map inside the card, as a fraction of its longer side. */
export const MINIMAP_PAD = 0.04;

/*
 * NOT HERE: how long the card stays bright after the camera stops (1.8 s). The card is
 * information about a MOVE — where did that pan take me — so it is at its most useful while
 * the camera moves and for a moment after, and clutter over the map the rest of the time. But
 * the 1.8 s is a CSS transition-delay in `styles/app.css`, not a number anything here reads,
 * and a constant nothing reads would only drift from the one that counts.
 */

/**
 * The fog over everything outside the frame, per theme.
 *
 * Not a design token: the stylesheet's `:root`/`.light` blocks are mirrored into
 * `theme/tokens.ts` and checked for drift, and a colour only one SVG path uses has no business
 * there. Dark ink over the light theme, black over the dark — a lighter fog over a dark map
 * reads as a spotlight on the wrong half.
 */
export const MINIMAP_FOG = {
  light: 'rgba(26,33,43,.16)',
  dark: 'rgba(0,0,0,.5)',
} as const;

/** A rectangle in world space, `{x, y}` its top-left corner. */
export interface WorldRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The card's size in CSS pixels. */
export interface MinimapBox {
  w: number;
  h: number;
}

const span = (lo: number, hi: number): number => Math.max(1, hi - lo);

/**
 * How big the card is for a map of this shape.
 *
 * The longest side is `side`; the other follows the map's aspect, clamped to the range above.
 * A map wider than tall gets a landscape card, a tall map a portrait one.
 */
export function minimapBox(b: Bounds, side: number = MINIMAP_SIDE): MinimapBox {
  const aspect = Math.min(
    MINIMAP_ASPECT_MAX,
    Math.max(MINIMAP_ASPECT_MIN, span(b.a, b.c) / span(b.b, b.d)),
  );
  return aspect >= 1
    ? { w: side, h: Math.round(side / aspect) }
    : { w: Math.round(side * aspect), h: side };
}

/**
 * The part of world space the card shows: the map's bounds plus padding, widened or
 * heightened to the card's own aspect so the SVG fills the card with nothing stretched.
 *
 * The extension is split evenly, so the map stays centred in the card whichever way it was
 * clamped.
 */
export function minimapViewBox(b: Bounds, box: MinimapBox, pad: number = MINIMAP_PAD): WorldRect {
  const p = Math.max(span(b.a, b.c), span(b.b, b.d)) * pad;
  let x = b.a - p;
  let y = b.b - p;
  let w = span(b.a, b.c) + 2 * p;
  let h = span(b.b, b.d) + 2 * p;
  const boxAspect = box.w / box.h;
  if (w / h > boxAspect) {
    const nh = w / boxAspect;
    y -= (nh - h) / 2;
    h = nh;
  } else {
    const nw = h * boxAspect;
    x -= (nw - w) / 2;
    w = nw;
  }
  return { x, y, w, h };
}

/** The `viewBox` attribute for a world rectangle. */
export const viewBoxAttr = (r: WorldRect): string => `${r.x} ${r.y} ${r.w} ${r.h}`;

/** World units per CSS pixel of the card — what a stroke or a radius meant in pixels scales by. */
export const minimapUnit = (vb: WorldRect, box: MinimapBox): number => vb.w / box.w;

/**
 * Where the frame is: the part of world space the stage is showing, which is the camera's
 * inverse. The camera maps world `p` to screen `p·k + {x,y}`, so the screen's corners map back
 * to `-x/k` and `(W - x)/k`.
 */
export function frameOf(view: Camera, stage: StageSize): WorldRect {
  return {
    x: -view.x / view.k,
    y: -view.y / view.k,
    w: stage.width / view.k,
    h: stage.height / view.k,
  };
}

/**
 * The fog: a path covering everything in the card EXCEPT the frame.
 *
 * One path with two subpaths and the even-odd rule — the outer rectangle far larger than any
 * viewBox will ever be, the inner one the frame — so it needs no clipping and no knowledge of
 * where the frame sits relative to the card's edges.
 */
export function fogPath(vb: WorldRect, frame: WorldRect): string {
  const big = 1e6;
  return (
    `M${vb.x - big},${vb.y - big}h${2 * big}v${2 * big}h${-2 * big}z ` +
    `M${frame.x},${frame.y}h${frame.w}v${frame.h}h${-frame.w}z`
  );
}

/**
 * The camera that puts world point `p` at the centre of the stage, at the zoom it has now.
 *
 * A click on the minimap means "take me there", not "zoom to there": the zoom is the reader's
 * and stays theirs.
 */
export function cameraCentredOn(p: { x: number; y: number }, stage: StageSize, k: number): Camera {
  return { x: stage.width / 2 - p.x * k, y: stage.height / 2 - p.y * k, k };
}
